import {Injectable} from "@angular/core";
import {BaseService} from "../services/base.service";
import {Observable, BehaviorSubject, of} from "rxjs";
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from "@angular/common/http";
import {catchError, tap, shareReplay} from "rxjs/operators";
import {MatSnackBar} from "@angular/material/snack-bar";
import {BasicLabManagerItemModel} from "../models/lab-manager/basic-lab-manager-item";
import {LabAgentModel} from "../models/lab-manager/lab-agent";
import {LabSubmissionModel} from "../models/lab-manager/lab-submission";
import {LabSubmissionDetailsModel} from "../models/lab-manager/lab-submission-details";
import {RepresentativeModel} from "../models/lab-manager/representative";
import {AgronimistModel} from "../models/lab-manager/agronimist";
import {UpdateOrderModel} from "../order-to-cash/models-otc/udate-order-model";
import {LabSampleModel} from "../models/lab-manager/lab-sample";
import {LabCostItemModel} from "../models/lab-manager/lab-cost-item";
import {AnalysisItemModel} from "../models/lab-manager/analysis-item";
import {Attachment} from "./models/attachment";
import {AttachmentModel} from "../order-to-cash/models-otc/attachment-model";
import { User } from "../models/user";

@Injectable({
    providedIn: 'root'
})
export class LabManagerService extends BaseService {

    private API_LAB_MANAGER_URL = this.API_ADMIN_BASE_URL + "v1/lab-management";

    private customersSubject = new BehaviorSubject<any[]>(null);
    customers$ = this.customersSubject.asObservable();

    constructor(
        private http: HttpClient,
        private snackBar: MatSnackBar) {
        super()
    }

    getSubmissionList(currentPage: number, pageSize: number, fromDate: string, toDate: string, searchText: string, isRepresentative: boolean, statusIds: number[], clientId: number, labAgentId: number, submissionType: number): Observable<any> {
        let params = new HttpParams()
            .set('page', currentPage.toString())
            .append('size', pageSize.toString());

        if (fromDate) {
            params = params.append('fromDate', fromDate);
        }

        if (toDate) {
            params = params.append('toDate', toDate);
        }

        if (searchText) {
            params = params.append('searchQuery', searchText);
        }

        if (isRepresentative != null) {
            params = params.append('isRep', isRepresentative);
        }

        if (clientId != null && clientId > 0) {
            params = params.append('clientId', clientId);
        }

        if (labAgentId != null && labAgentId > 0) {
            params = params.append('agentId', labAgentId);
        }

        if (submissionType != null && submissionType > 0) {
            params = params.append('typeId', submissionType);
        }

        if (statusIds?.length > 0) {
            statusIds.forEach((element)=> {
                params = params.append('statusIds', element.toString());
            })
        }

        return this.http.get<any>(this.API_LAB_MANAGER_URL + '/submissions/list', {params}).pipe(
            catchError((err) => {
                this.hasError.next(true);
                this.snackBar.open('Error Retrieving Lab Manager Submission List','close', {
                    duration: 5000,
                });
                this.hasError.next(false);
                return this.errorHandler(err);
            })
        );
    }

    getLabManagerStatusList(): Observable<BasicLabManagerItemModel[]> {
        return this.http.get<BasicLabManagerItemModel[]>(this.API_LAB_MANAGER_URL + '/status/list');
    }

    getLabAgentListForAgronomist(): Observable<User[]> {
        return this.http.get<User[]>(this.API_LAB_MANAGER_URL + '/users/labagent');
    }


    removeLabSample(sampleId: number) {
        return this.http.delete<any>(this.API_LAB_MANAGER_URL + `/samples/${sampleId}`).pipe(
            catchError((err) => {
                this.hasError.next(true);
                this.snackBar.open("Error Deleting Lab Sample", "close", {
                    duration: 5000,
                });
                this.hasError.next(false);
                return this.errorHandler(err);
            })
        );
    }

    addFileToLabResult(submissionId: number, description: string, fileObj: File) {
        const formData = new FormData();
        formData.append('file', fileObj);

        return this.http.post<any>(this.API_LAB_MANAGER_URL + `/submissions/${submissionId}/attachments?description=${description}`, formData);
    }

    retrieveLabResultAttachments(submissionId: number) {
        return this.http.get<Attachment[]>(this.API_LAB_MANAGER_URL + `/submissions/${submissionId}/attachments`);
    }

    downloadLabResultAttachment(attachmentId: number): Observable<Blob> {
        return this.http.get(this.API_LAB_MANAGER_URL + `/submissions/attachments/${attachmentId}/download`, { responseType: 'blob' } )
    }

    deleteLabResultAttachment(attachmentId: number) {
        return this.http.delete(this.API_LAB_MANAGER_URL + `/submissions/attachments/${attachmentId}`,{ responseType: 'text'} );
    }

    updateLabResultAttachmentDescription(id: number, description: string): Observable<AttachmentModel> {
        return this.http.put<Attachment>(this.API_LAB_MANAGER_URL +`/submissions/attachments` , {id:id,description:description})
    }

    addLabAnalysis(analysisBody: any) {
        return this.http.post<any>(this.API_LAB_MANAGER_URL + '/analysis', analysisBody);
    }

    addToAllLabAnalyses(analysisBody: any) {
        return this.http.post<any>(this.API_LAB_MANAGER_URL + '/analysis/all', analysisBody).pipe(
            catchError((err) => {
                this.hasError.next(true);
                this.snackBar.open('Error Applying Analysis Changes','close', {
                    duration: 5000,
                });
                this.hasError.next(false);
                return this.errorHandler(err);
            })
        );
    }

    removeLabAnalysis(sampleId: number) {
        let params = new HttpParams()
            .set('sampleId', sampleId.toString());

        return this.http.delete<any>(this.API_LAB_MANAGER_URL + '/analysis', {params});
    }

    removeAllLabAnalyses(labSubmissionId: number) {
        let params = new HttpParams()
            .set('submissionId', labSubmissionId.toString());

        return this.http.delete<any>(this.API_LAB_MANAGER_URL + '/analysis/all', {params});
    }

    removeAllSamples(labSubmissionId: number) {
        let params = new HttpParams()
            .set('submissionId', labSubmissionId.toString());

        return this.http.delete<any>(this.API_LAB_MANAGER_URL + '/samples/all', {params});
    }

    getSoftTissueLabResults(submissionId: number, currentPage: number, pageSize: number) {
        let params = new HttpParams()
            .set('page', currentPage.toString())
            .append('size', pageSize.toString());
        return this.http.get<any>(this.API_LAB_MANAGER_URL + '/submissions/results/' + submissionId.toString(), {params});
    }

    getSoilLabResults(submissionId: number, currentPage: number, pageSize: number) {
        let params = new HttpParams()
            .set('page', currentPage.toString())
            .append('size', pageSize.toString());
        return this.http.get<any>(this.API_LAB_MANAGER_URL + '/soil/results/' + submissionId.toString(), {params});
    }

    getLabSamplesListById(submissionId: number, currentPage: number, pageSize: number, fromDate: string, toDate: string, searchText: string): Observable<LabSampleModel[]> {
        let params = new HttpParams()
            .set('page', currentPage.toString())
            .append('size', pageSize.toString());

        if (fromDate) {
            params = params.append('fromDate', fromDate);
        }

        if (toDate) {
            params = params.append('toDate', toDate);
        }

        if (searchText) {
            params = params.append('searchQuery', searchText);
        }

        return this.http.get<LabSampleModel[]>(this.API_LAB_MANAGER_URL + `/samples/${submissionId}`, {params}).pipe(
            catchError((err) => {
                this.hasError.next(true);
                this.snackBar.open('Error Retrieving Lab Manager Submission List','close', {
                    duration: 5000,
                });
                this.hasError.next(false);
                return this.errorHandler(err);
            })
        );
    }

    getLabSubmissionCalculator(submissionId: number): Observable<LabCostItemModel[]> {
        return this.http.get<LabCostItemModel[]>(this.API_LAB_MANAGER_URL + `/submissions/${submissionId}/calculator`);
    }

    getLabSamplesList(tenantId: number, pinTypeId: number, currentPage: number, pageSize: number, fromDate: string, toDate: string, searchText: string, selectedSampleGroups: number[]): Observable<LabSampleModel[]> {
        let params = new HttpParams()
            .set('tenantId', tenantId.toString())
            .append('pinTypeId', pinTypeId.toString())
            .append('page', currentPage.toString())
            .append('size', pageSize.toString());

        if (fromDate) {
            params = params.append('fromDate', fromDate);
        }

        if (toDate) {
            params = params.append('toDate', toDate);
        }

        if (searchText) {
            params = params.append('searchQuery', searchText);
        }

        if(selectedSampleGroups.length > 0) {
            for(let sampleGroup of selectedSampleGroups) {
                params = params.append('sampleGroups', sampleGroup.toString());
            }
        }

        return this.http.get<LabSampleModel[]>(this.API_LAB_MANAGER_URL + '/samples', {params}).pipe(
            catchError((err) => {
                if(err?.status === 404){
                    this.snackBar.open('No Samples found for Tenant','close', {
                        duration: 5000,
                    });
                } else {
                    this.snackBar.open('Error Retrieving Lab Manager Submission List','close', {
                        duration: 5000,
                    });
                }
                this.hasError.next(true);
                this.hasError.next(false);
                return this.errorHandler(err);
            })
        );
    }

    getSampleGroupList(tenantId: number): Observable<BasicLabManagerItemModel[]> {
        let params = new HttpParams()
            .set('tenantId', tenantId.toString())
        return this.http.get<BasicLabManagerItemModel[]>(this.API_LAB_MANAGER_URL + '/samples/groups', {params});
    }

    getAnalysisItems(labId: number, typeId: number): Observable<BasicLabManagerItemModel[]> {
        let params = new HttpParams()
            .set('labId', labId.toString())
            .append('typeId', typeId.toString());

        return this.http.get<BasicLabManagerItemModel[]>(this.API_LAB_MANAGER_URL + '/meta/items', {params});
    }

    approveLabResults(submissionId: number) {
        return this.http.put<any>(this.API_LAB_MANAGER_URL + `/submissions/${submissionId}/approve`, {});
    }

    downloadLabSubmissionExcel(submissionId: number, languageType: number): Observable<HttpResponse<Blob>> {
        let params = new HttpParams()
            .set('languageType', languageType)
            .append('protectSheet', true);

        return this.http.get<Blob>(this.API_LAB_MANAGER_URL + `/submissions/${submissionId}/report`, {
            observe: 'response',
            responseType: 'blob' as 'json',
            params: params

        });
    }

    saveAnalysisCostItem(costItem: LabCostItemModel) {
        return this.http.put<any>(this.API_LAB_MANAGER_URL + `/submissions/item/${costItem.id}`, costItem);
    }

    getLabSubmissionDetails(labSubmissionId): Observable<LabSubmissionDetailsModel> {
        return this.http.get<LabSubmissionDetailsModel>(this.API_LAB_MANAGER_URL + `/submissions/${labSubmissionId}`);
    }

    getLabAnalysisList(labManSubmissionId: string, currentPage: number, pageSize: number, searchText: string): Observable<any> {
        let params = new HttpParams()
            .set('submissionId', labManSubmissionId)
            .append('page', currentPage.toString())
            .append('size', pageSize.toString());

        if (searchText) {
            params = params.append('searchQuery', searchText);
        }

        return this.http.get<any>(this.API_LAB_MANAGER_URL + '/analysis/samples', {params})
    }

    addLabSample(sampleId: number, labManSubmissionId: number, pinSamplesId: number) {
        return this.http.post<any>(this.API_LAB_MANAGER_URL + '/samples', {
            
            labManSubmissionId: labManSubmissionId,
            pinSamplesId: sampleId
        });
    }

    checkLabSubmissionSampleValidity(submissionId: number) {
        return this.http.get<boolean>(this.API_LAB_MANAGER_URL + `/samples/${submissionId}/valid`);
    }

    checkLabSubmissionAnalysisValidity(submissionId: number) {
        return this.http.get<any>(this.API_LAB_MANAGER_URL + `/analysis/${submissionId}/valid`);
    }

    addLabSubmission(submissionTypeId: number, representativeId: number, isRepresentative: boolean) {
        let url = `/submissions/${submissionTypeId}`;

        if(isRepresentative) {
            url += `?representativeId=${representativeId}`
        }
        return this.http.post<any>(this.API_LAB_MANAGER_URL + url, {}).pipe(
            catchError((err) => {
                this.hasError.next(true);
                this.snackBar.open('Error Adding New Lab Manager Submission','close', {
                    duration: 5000,
                });
                this.hasError.next(false);
                return this.errorHandler(err);
            })
        );
    }

    uploadSoftTissueFile(submissionId: number, labId: number, typeId: number, file: File, isCsv: boolean) {
        let formData = new FormData();
        formData.append('file', file);

        let params = new HttpParams()
            .set('labId', labId)
            .append('typeId', typeId.toString())

        let url = isCsv ? `/submissions/${submissionId}/csv/upload` : `/submissions/${submissionId}/upload`;

        return this.http.post(this.API_LAB_MANAGER_URL + url, formData, {params});
    }

    uploadSoilFile(submissionId: number, file: File, isCsv: boolean) {
        let formData = new FormData();
        formData.append('file', file);

        let params = new HttpParams()
            .set('submissionId', submissionId)

        let url = isCsv ? `/soil/csv/upload` : `/soil/upload`;

        return this.http.post(this.API_LAB_MANAGER_URL + url, formData, {params});
    }

    getSubmissionTypes(): Observable<BasicLabManagerItemModel[]> {
        return this.http.get<BasicLabManagerItemModel[]>(this.API_LAB_MANAGER_URL + '/meta/types');
    }

    getLabRepresentatives(userId: number): Observable<RepresentativeModel[]> {
        return this.http.get<RepresentativeModel[]>(this.API_LAB_MANAGER_URL + `/meta/representative/${userId}`);
    }

    getAgronomistList(): Observable<AgronimistModel[]> {
        return this.http.get<AgronimistModel[]>(this.API_LAB_MANAGER_URL + '/meta/agronomist');
    }

    getLabList(): Observable<BasicLabManagerItemModel[]> {
        return this.http.get<BasicLabManagerItemModel[]>(this.API_LAB_MANAGER_URL + '/meta/labs');
    }

    updateLabSubmissionDetail(labSubmissionDetail: LabSubmissionDetailsModel, labSubmissionId: number) {
        return this.http.put<LabSubmissionDetailsModel>(this.API_LAB_MANAGER_URL + `/submissions/${labSubmissionId}`, labSubmissionDetail)
            .pipe(
                catchError((err) => {
                    this.hasError.next(true);
                    this.snackBar.open("Error Updating Lab Submission Details", "close", {
                        duration: 5000,
                    });
                    this.hasError.next(false);
                    return this.errorHandler(err);
                })
            );
    }

    deleteLabSubmission(labSubmissionId: number){
        return this.http.delete<any>(this.API_LAB_MANAGER_URL + `/submissions/${labSubmissionId}`).pipe(
            catchError((err) => {
                this.hasError.next(true);
                this.snackBar.open("Error Deleting Lab Submission", "close", {
                    duration: 5000,
                });
                this.hasError.next(false);
                return this.errorHandler(err);
            })
        );
    }

  getSoilTextureValues() {
    return this.http.get<any[]>(this.API_LAB_MANAGER_URL + "/meta/soil-texture-values");
  }

  private customersCache$: Observable<any[]> = null;

  getCustomers(): Observable<any[]> {
    if (!this.customersCache$) {
      this.customersCache$ = this.http.get<any[]>(this.API_LAB_MANAGER_URL + "/meta/customers").pipe(
        tap(res => this.customersSubject.next(res)),
        shareReplay(1)
      );
    }
    return this.customersCache$;
  }

  getSubmissionCustomers(submissionId: number): Observable<{customer: any, payer: any}> {
    return this.http.get<{customer: any, payer: any}>(this.API_LAB_MANAGER_URL + "/meta/customers/submission/" + submissionId);
  }

  refreshCustomers(): void {
    this.customersSubject.next(null);
    this.customersCache$ = null;
  }

  // === Multi-Sample Merge ===

  mergeSamples(submissionId: number, primarySampleId: number, linkedSampleIds: number[]) {
    return this.http.post<any>(this.API_LAB_MANAGER_URL + '/samples/merge', {
      submissionId,
      primarySampleId,
      linkedSampleIds
    });
  }

  unmergeSamples(primarySampleId: number) {
    return this.http.delete<any>(this.API_LAB_MANAGER_URL + `/samples/merge/${primarySampleId}`);
  }

  getMergeGroups(submissionId: number): Observable<any[]> {
    return this.http.get<any[]>(this.API_LAB_MANAGER_URL + `/samples/${submissionId}/merges`);
  }
}
